/**
 * Media Plan Builder API client.
 *
 * Backend: backend/src/routes/mediaPlanAdmin.ts, mounted at
 * /api/admin/media-plans. Every read and the plan flow (quote → create →
 * status / export / PDF, plus registering a NEW advertiser) need `sales.view`;
 * editing rate cards or existing advertiser records needs `mediaPlans.manage`.
 *
 * Envelopes are unwrapped here; non-2xx responses surface as ApiError from
 * apiRequest except where a typed outcome is more useful (quote, create
 * plan, create advertiser, sheet export).
 */

import { API_BASE_URL, ApiError, apiRequest, getAuthToken } from './api';
import type {
  AdvertiserPricingHistory,
  AdvertiserRecord,
  CreatePlanResult,
  GuardrailFinding,
  MediaPlanDoc,
  MediaPlanDraftDoc,
  MediaPlanQuote,
  MediaPlanRateCard,
  MediaPlanReference,
  MediaPlanStatus,
  NeedsAdvertiser,
  NeedsAdvertiserStub,
  PdfCardData,
  PlanInput,
  RateCardInput,
  SheetExportResult,
} from '../components/Sales/MediaPlans/types';

const BASE = '/api/admin/media-plans';

const isApiError = (err: unknown): err is ApiError => !!err && typeof err === 'object' && typeof (err as ApiError).status === 'number';

// --- Reference + registry ----------------------------------------------------------

export async function getMediaPlanReference(): Promise<MediaPlanReference> {
  return apiRequest(`${BASE}/reference`);
}

export async function listAdvertisers(): Promise<AdvertiserRecord[]> {
  const body = await apiRequest(`${BASE}/advertisers`);
  return body.advertisers ?? [];
}

export async function getAdvertiserPricingHistory(key: string): Promise<AdvertiserPricingHistory> {
  const body = await apiRequest(`${BASE}/advertisers/${encodeURIComponent(key)}/pricing-history`);
  return body.history;
}

export interface NewAdvertiserInput {
  name: string;
  categoryKey: string;
  aliases?: string[];
  gamAdvertiserIds?: string[];
  agencyKey?: string;
  parentCompany?: string;
  notes?: string;
  force?: boolean;
}

/** 409 from POST /advertisers: the name already exists, or loosely matches existing records. */
export class AdvertiserConflictError extends Error {
  constructor(
    public readonly kind: 'already_exists' | 'possible_duplicates',
    public readonly candidates: Array<{ key: string; name: string; categoryKey?: string }>,
    public readonly existing?: AdvertiserRecord,
  ) {
    super(kind === 'already_exists' ? 'This advertiser is already in the registry.' : 'Similar advertisers already exist.');
    this.name = 'AdvertiserConflictError';
  }
}

export async function createAdvertiser(input: NewAdvertiserInput): Promise<AdvertiserRecord> {
  try {
    const body = await apiRequest(`${BASE}/advertisers`, { method: 'POST', body: JSON.stringify(input) });
    return body.advertiser;
  } catch (err) {
    if (isApiError(err) && err.status === 409 && err.body) {
      if (err.body.error === 'already_exists') {
        const existing = err.body.advertiser as AdvertiserRecord | undefined;
        throw new AdvertiserConflictError('already_exists', existing ? [{ key: existing.key, name: existing.name, categoryKey: existing.categoryKey }] : [], existing);
      }
      if (err.body.error === 'possible_duplicates') throw new AdvertiserConflictError('possible_duplicates', err.body.candidates ?? []);
    }
    throw err;
  }
}

export type AdvertiserPatch = Omit<Partial<AdvertiserRecord>, 'negotiatedRateCardId' | 'resoldVia'> & {
  /** null clears the pointer. */
  negotiatedRateCardId?: string | null;
  resoldVia?: 'revolt' | 'ione' | null;
  /**
   * Appended to the stored aliases and deduped server-side, so it never races
   * an editor that sends the whole `aliases` list (Creators → Brand logos → Same as…).
   */
  addAliases?: string[];
};

export async function updateAdvertiser(key: string, patch: AdvertiserPatch): Promise<AdvertiserRecord> {
  const body = await apiRequest(`${BASE}/advertisers/${encodeURIComponent(key)}`, { method: 'PUT', body: JSON.stringify(patch) });
  return body.advertiser;
}

export type ResolveAdvertiserOutcome =
  | { kind: 'existing'; advertiser: AdvertiserRecord; matchedBy: string }
  | { kind: 'ambiguous'; candidates: Array<{ key: string; name: string }> }
  | { kind: 'new'; needsAdvertiser: NeedsAdvertiser };

/**
 * The new-advertiser draft for a typed name: the record it already matches,
 * several close matches, or a category guess plus matching GAM companies.
 * Slow (Claude + GAM), so call it only when the seller asks to add someone.
 */
export async function resolveAdvertiser(name: string, agency?: string): Promise<ResolveAdvertiserOutcome> {
  const body = await apiRequest(`${BASE}/advertisers/resolve`, { method: 'POST', body: JSON.stringify({ name, agency: agency || undefined }) });
  if (body.advertiser) return { kind: 'existing', advertiser: body.advertiser, matchedBy: body.matchedBy };
  if (body.candidates) return { kind: 'ambiguous', candidates: body.candidates };
  return { kind: 'new', needsAdvertiser: body.needsAdvertiser };
}

// --- Quote → create ----------------------------------------------------------------

export type QuoteOutcome =
  | { kind: 'quote'; quote: MediaPlanQuote }
  | { kind: 'needsAdvertiser'; needsAdvertiser: NeedsAdvertiserStub }
  | { kind: 'ambiguous'; error: string; candidates: Array<{ key: string; name: string }> };

/** Price a brief. Nothing is saved; a name that isn't in the registry comes back as `needsAdvertiser`. */
export async function quoteMediaPlan(input: PlanInput, opts: { signal?: AbortSignal } = {}): Promise<QuoteOutcome> {
  try {
    // No `applySuggestedRates`: its presence makes the server guess an unknown advertiser's category on every quote.
    const body = await apiRequest(`${BASE}/plans/quote`, { method: 'POST', body: JSON.stringify(input), signal: opts.signal });
    if (body.needsAdvertiser) return { kind: 'needsAdvertiser', needsAdvertiser: body.needsAdvertiser };
    return { kind: 'quote', quote: body.quote };
  } catch (err) {
    if (isApiError(err) && err.status === 422 && err.body?.reason === 'ambiguous') {
      return { kind: 'ambiguous', error: err.body.error, candidates: err.body.candidates ?? [] };
    }
    throw err;
  }
}

/** 409 `quote_changed`: prices moved between the quote the seller reviewed and create. Carries the fresh quote. */
export class PricesChangedError extends Error {
  constructor(public readonly quote: MediaPlanQuote) {
    super('Prices changed since this quote. Review the new numbers, then create the plan.');
    this.name = 'PricesChangedError';
  }
}

/** 422 `reason_required`: a flagged rate has no internal reason (or one outside 10–500 characters). */
export class ReasonsRequiredError extends Error {
  constructor(
    message: string,
    public readonly findings: GuardrailFinding[],
    /** Finding ids still missing a reason. */
    public readonly missing: string[],
  ) {
    super(message);
    this.name = 'ReasonsRequiredError';
  }
}

export type PlanRevisionErrorCode = 'not_found' | 'forbidden' | 'already_revised';

/** A "new version" the server refused: the plan is gone, isn't yours, or already has a newer version (`newerPlanId`). */
export class PlanRevisionError extends Error {
  constructor(
    message: string,
    public readonly code: PlanRevisionErrorCode,
    public readonly newerPlanId?: string,
  ) {
    super(message);
    this.name = 'PlanRevisionError';
  }
}

export interface CreatePlanOptions {
  createSheet: boolean;
  revisesPlanId?: string;
  /** Same id for the same body: a retry after a lost response returns the plan already made. */
  requestId: string;
  /** The `fingerprint` of the quote the seller reviewed. */
  quoteFingerprint?: string;
  /** Guardrail finding id → the seller's internal reason. */
  pricingNotes?: Record<string, string>;
}

/** Waits between tries while an earlier click's create is still running (409 `in_progress`). */
export const CREATE_IN_PROGRESS_RETRY_MS = [1000, 2000, 4000];

const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

export async function createMediaPlan(input: PlanInput, opts: CreatePlanOptions): Promise<CreatePlanResult & { replayed?: boolean }> {
  const payload = JSON.stringify({
    ...input,
    createSheet: opts.createSheet,
    revisesPlanId: opts.revisesPlanId,
    requestId: opts.requestId,
    quoteFingerprint: opts.quoteFingerprint,
    pricingNotes: opts.pricingNotes && Object.keys(opts.pricingNotes).length ? opts.pricingNotes : undefined,
  });
  for (let attempt = 0; ; attempt += 1) {
    try {
      const body = await apiRequest(`${BASE}/plans`, { method: 'POST', body: payload });
      return {
        plan: body.plan,
        sheet: body.sheet ?? null,
        sheetError: body.sheetError ?? null,
        history: body.history ?? null,
        agencyLabel: body.agencyLabel,
        replayed: body.replayed === true || undefined,
      };
    } catch (err) {
      if (!isApiError(err)) throw err;
      if (err.code === 'in_progress' && attempt < CREATE_IN_PROGRESS_RETRY_MS.length) {
        await wait(CREATE_IN_PROGRESS_RETRY_MS[attempt]);
        continue;
      }
      if (err.code === 'quote_changed' && err.body?.quote) throw new PricesChangedError(err.body.quote);
      if (err.code === 'reason_required') throw new ReasonsRequiredError(err.message, err.body?.findings ?? [], err.body?.missing ?? []);
      if (err.code === 'not_found' || err.code === 'forbidden' || err.code === 'already_revised') {
        throw new PlanRevisionError(err.message, err.code, err.body?.newerPlanId);
      }
      throw err;
    }
  }
}

/** A brief priced in Culture Genius chat; null once it has expired (14 days) or for anyone but its creator. */
export async function getMediaPlanDraft(id: string): Promise<MediaPlanDraftDoc | null> {
  try {
    const body = await apiRequest(`${BASE}/drafts/${encodeURIComponent(id)}`);
    return body.draft ?? null;
  } catch (err) {
    if (isApiError(err) && err.status === 404) return null;
    throw err;
  }
}

/** A requestId for POST /plans: 8–100 of [A-Za-z0-9_-]. */
export function newRequestId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  return `req-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

// --- Plans ----------------------------------------------------------------------------

export async function listMediaPlans(opts: { mine?: boolean; advertiserKey?: string; limit?: number } = {}): Promise<MediaPlanDoc[]> {
  const params = new URLSearchParams();
  if (opts.mine) params.set('mine', 'true');
  if (opts.advertiserKey) params.set('advertiserKey', opts.advertiserKey);
  if (opts.limit) params.set('limit', String(opts.limit));
  const qs = params.toString();
  const body = await apiRequest(`${BASE}/plans${qs ? `?${qs}` : ''}`);
  return body.plans ?? [];
}

export async function getMediaPlan(id: string): Promise<{ plan: MediaPlanDoc; agencyLabel?: string }> {
  const body = await apiRequest(`${BASE}/plans/${encodeURIComponent(id)}`);
  return { plan: body.plan, agencyLabel: body.agencyLabel };
}

export async function setMediaPlanStatus(id: string, status: Exclude<MediaPlanStatus, 'superseded'>): Promise<MediaPlanDoc> {
  const body = await apiRequest(`${BASE}/plans/${encodeURIComponent(id)}/status`, { method: 'PATCH', body: JSON.stringify({ status }) });
  return body.plan;
}

/** Thrown when the user's Google Workspace connection needs to be (re)authorized. */
export class GoogleReconnectRequiredError extends Error {
  constructor(message?: string) {
    super(message || 'Google Workspace needs to be reconnected before a Sheet can be created.');
    this.name = 'GoogleReconnectRequiredError';
  }
}

export async function exportMediaPlanSheet(id: string): Promise<SheetExportResult> {
  try {
    const body = await apiRequest(`${BASE}/plans/${encodeURIComponent(id)}/export`, { method: 'POST' });
    return body.sheet;
  } catch (err) {
    if (isApiError(err) && err.status === 409) throw new GoogleReconnectRequiredError(err.message);
    throw err;
  }
}

export interface GeneratedPdf {
  pdf: PdfCardData;
  downloadUrl: string;
}

export async function generateMediaPlanPdf(id: string, opts: { tierIndex?: number; includeNotes?: boolean } = {}): Promise<GeneratedPdf> {
  const body = await apiRequest(`${BASE}/plans/${encodeURIComponent(id)}/pdf`, {
    method: 'POST',
    body: JSON.stringify({ tierIndex: opts.tierIndex ?? 0, includeNotes: opts.includeNotes ?? false }),
  });
  return { pdf: body.pdf, downloadUrl: body.downloadUrl };
}

export async function saveMediaPlanAsRateCard(id: string, opts: { name?: string; effectiveFrom?: string; effectiveTo?: string; activate?: boolean } = {}): Promise<MediaPlanRateCard> {
  const body = await apiRequest(`${BASE}/plans/${encodeURIComponent(id)}/save-as-rate-card`, { method: 'POST', body: JSON.stringify(opts) });
  return body.rateCard;
}

/** Fetch an authenticated binary and hand it to the browser as a download. */
export async function downloadAuthedFile(path: string, fileName: string): Promise<void> {
  const token = await getAuthToken();
  const response = await fetch(`${API_BASE_URL}${path}`, { headers: { Authorization: `Bearer ${token}` } });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.error || `Download failed (${response.status})`);
  }
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function downloadMediaPlanPdf(generated: GeneratedPdf): Promise<void> {
  return downloadAuthedFile(generated.downloadUrl, generated.pdf.fileName);
}

// --- Rate cards ----------------------------------------------------------------------------

export async function listRateCards(): Promise<MediaPlanRateCard[]> {
  const body = await apiRequest(`${BASE}/rate-cards`);
  return body.rateCards ?? [];
}

export async function getRateCard(id: string): Promise<MediaPlanRateCard> {
  const body = await apiRequest(`${BASE}/rate-cards/${encodeURIComponent(id)}`);
  return body.rateCard;
}

export async function createRateCard(input: RateCardInput): Promise<MediaPlanRateCard> {
  const body = await apiRequest(`${BASE}/rate-cards`, { method: 'POST', body: JSON.stringify(input) });
  return body.rateCard;
}

export async function updateRateCard(id: string, input: RateCardInput): Promise<MediaPlanRateCard> {
  const body = await apiRequest(`${BASE}/rate-cards/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify(input) });
  return body.rateCard;
}
